/**
 * Product Detail Page - iPad-Optimized
 * 
 * Displays detailed information about an insurance product
 * Including company info, product materials, and insurance company resources
 * 
 * Touch targets: All buttons ≥44x44px
 * Layout: Breadcrumb navigation + Two-column layout
 */

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Input,
  Breadcrumb,
  Card,
  Typography,
  Space,
  Divider,
  Avatar,
} from "antd";
import {
  SearchOutlined,
  GlobalOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  PercentageOutlined,
  DollarOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";
import CustomButton from "../components/CustomButton";
import "./ProductDetail.css";

const { Title, Text, Paragraph } = Typography;

// Mock product data
const mockProduct = {
  id: "1",
  name: "裕滿人生保障計劃2",
  company: {
    name: "友邦保險 · AIA",
    logo: "AIA",
    website: "https://www.aia.com.hk",
    updateTime: "2024-11-36 15:00",
    description: `友邦保險有限公司 是泛亞地區最大的獨立上市人壽保險集團,致力於為個人及企業提供高品質的產品及服務,涵蓋人壽保險,醫療保險,重大疾病保險,意外傷害保險以及養老保險。友邦保險有限公司是泛亞地區最大的獨立上市人壽保險集團,致力於為個人及企業提供高品質的產品及服務,涵蓋人壽保險,醫療保險,重大疾病保險,意外傷害保險以及養老保險。

友邦保險有限公司是泛亞地區最大的獨立上市人壽保險集團,致力於為個人及企業提供高品質的產品及服務,涵蓋人壽保險,醫療保險,重大疾病保險,意外傷害保險以及養老保險。

友邦保險有限公司是泛亞地區最大的獨立上市人壽保險集團,致力於為個人及企業提供高品質的產品及服務,涵蓋人壽保險,醫療保險,重大疾病保險,意外傷害保險以及養老保險...`,
  },
};

export function ProductDetail() {
  const { productId: _productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  const handleBack = () => {
    navigate("/products");
  };

  return (
    <div className="product-detail-container">
      {/* Top Navigation Bar */}
      <div className="product-detail-navbar">
        <Breadcrumb
          items={[
            {
              title: (
                <span className="breadcrumb-link" onClick={handleBack}>
                  產品列表
                </span>
              ),
            },
            {
              title: mockProduct.name,
            },
          ]}
        />
        <Input
          placeholder="輸入關鍵詞搜尋產品"
          prefix={<SearchOutlined />}
          style={{ width: 320 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {/* Main Content */}
      <div className="product-detail-content">
        <div className="detail-layout">
          {/* Left Column - Company Info */}
          <div className="detail-left">
            <Card className="company-info-card">
              <Title level={4}>保司介紹</Title>
              <Divider />

              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {/* Company Header */}
                <Space align="center" size="middle">
                  <Avatar
                    size={64}
                    style={{
                      backgroundColor: "#fff",
                      border: "1px solid #f0f0f0",
                      fontSize: 24,
                      fontWeight: "bold",
                      color: "#ff4d4f",
                    }}>
                    {mockProduct.company.logo}
                  </Avatar>
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {mockProduct.company.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 14 }}>
                      保險公司資料檢查時間：{mockProduct.company.updateTime}
                    </Text>
                  </div>
                </Space>

                {/* Company Website */}
                <div className="company-website">
                  <GlobalOutlined style={{ fontSize: 18, marginRight: 8 }} />
                  <Text>官網</Text>
                  <a
                    href={mockProduct.company.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginLeft: 16 }}>
                    {mockProduct.company.website}
                  </a>
                </div>

                {/* Company Description */}
                <div>
                  <Paragraph
                    ellipsis={{
                      rows: 6,
                      expandable: true,
                      symbol: "展開",
                    }}
                    style={{ marginBottom: 0 }}>
                    {mockProduct.company.description}
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </div>

          {/* Right Column - Resources */}
          <div className="detail-right">
            {/* Product Resources */}
            <Card className="resources-card">
              <Title level={4}>產品資料</Title>
              <Divider />
              <div className="resource-grid">
                <CustomButton
                  className="resource-item"
                  icon={<FileTextOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>產品資料</Text>
                </CustomButton>
                <CustomButton
                  className="resource-item"
                  icon={<FilePdfOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>產品條款</Text>
                </CustomButton>
              </div>
            </Card>

            {/* Insurance Company Resources */}
            <Card className="resources-card" style={{ marginTop: 16 }}>
              <Title level={4}>保司資料</Title>
              <Divider />
              <div className="resource-grid">
                <CustomButton
                  className="resource-item"
                  icon={<FileTextOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>保司介紹</Text>
                </CustomButton>
                <CustomButton
                  className="resource-item"
                  icon={<PercentageOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>產品優惠</Text>
                </CustomButton>
                <CustomButton
                  className="resource-item"
                  icon={<DollarOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>費用指引</Text>
                </CustomButton>
                <CustomButton
                  className="resource-item"
                  icon={<FileProtectOutlined style={{ fontSize: 24 }} />}
                  bgColor="transparent"
                >
                  <Text>行政資料</Text>
                </CustomButton>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

